// 全局登录态:App 顶层挂载,启动时查一次 /api/auth/me。
// 关键区分:unauthenticated 仅在明确 401 时为 true —— 网络抖动 / 后端未升级(404)不算
// "未登录",避免把用户误踢到登录页造成死循环。
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { authApi } from "@/lib/authApi";
import { CREDITS_CHANGED_EVENT } from "@/lib/http";
import type { AuthUser } from "@/types";

interface AuthState {
  /** 当前用户;未登录 / 尚未加载完成时为 null */
  user: AuthUser | null;
  /** 首次 me 请求尚未返回 */
  loading: boolean;
  /** 明确收到 401(需要登录);守卫据此跳转 /auth */
  unauthenticated: boolean;
  /** 重新拉取 me(次数变化后调用,刷新顶栏徽章) */
  refresh: () => Promise<AuthUser | null>;
  /** 登出:删服务端 session 并清本地状态 */
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [unauthenticated, setUnauthenticated] = useState(false);
  // 防竞态:并发 refresh 时只采纳最后一次的结果。
  const seqRef = useRef(0);

  const refresh = useCallback(async (): Promise<AuthUser | null> => {
    const seq = ++seqRef.current;
    const res = await authApi.me();
    if (seq !== seqRef.current) return null; // 已有更新的请求在途,丢弃本次结果
    if (res.ok) {
      setUser(res.data);
      setUnauthenticated(false);
      setLoading(false);
      return res.data;
    }
    // 只有明确 401 才判定为未登录;其他错误(网络/5xx)保留现有 user,避免误踢。
    if (res.status === 401) {
      setUser(null);
      setUnauthenticated(true);
    }
    setLoading(false);
    return null;
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout(); // 失败也继续清本地态——用户明确要走,别拦着
    seqRef.current++; // 作废在途的 me 结果
    setUser(null);
    setUnauthenticated(true);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 计费端点(体检/查假/比对…)成功或 402 后 http.ts 会派发此事件 → 刷新顶栏次数徽章。
  // 没有这一条,用户跑完体检徽章还显示旧余额,直到撞 402 才发现钱不够。
  useEffect(() => {
    const onCreditsChanged = () => {
      refresh();
    };
    window.addEventListener(CREDITS_CHANGED_EVENT, onCreditsChanged);
    return () => window.removeEventListener(CREDITS_CHANGED_EVENT, onCreditsChanged);
  }, [refresh]);

  const value = useMemo(
    () => ({ user, loading, unauthenticated, refresh, logout }),
    [user, loading, unauthenticated, refresh, logout]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return ctx;
}
